import { useEffect, useRef } from 'react';
import { useEvoFeedbackCollectorUrl } from '@/hooks/use-feedback-collector-url';
import type { EvoFeatureFeedbackCollectorProps } from '@/types/feedbackCollector';

/**
 * Evo version of the feedback collector component that wraps the oc-component.
 *
 * This component automatically sources:
 * - product, baseUrl, and accountToken from configuration
 *
 * Creates the `oc-component` element imperatively and sets the `href` attribute
 * BEFORE appending it to the DOM. This is required because the OC client's
 * `connectedCallback` reads `getAttribute('href')` synchronously when the element
 * is connected. React 19 ref callbacks fire after DOM insertion, which is too late.
 *
 * The `feature` prop is required, other props are optional with sensible defaults.
 *
 * @example
 * ```tsx
 * <EvoFeatureFeedbackCollector feature="Dashboard" />
 * ```
 */
export const EvoFeatureFeedbackCollector: React.FC<EvoFeatureFeedbackCollectorProps> = ({
  feature,
  className,
  customerName,
  userId,
  reDisplayAfterDays,
}) => {
  const url = useEvoFeedbackCollectorUrl({
    feature,
    customerName,
    userId,
    reDisplayAfterDays,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!url || !container) return;

    // Create the oc-component element imperatively so the href attribute
    // is present BEFORE the element is connected to the DOM. The OC client's
    // connectedCallback reads getAttribute('href') synchronously on connection
    // and does not define attributeChangedCallback, so the attribute must
    // already be set at insertion time.
    const ocElement = document.createElement('oc-component');
    ocElement.setAttribute('href', url);
    container.appendChild(ocElement);

    return () => {
      container.innerHTML = '';
    };
  }, [url]);

  if (!url) {
    return null;
  }

  return <div ref={containerRef} className={className} />;
};

export default EvoFeatureFeedbackCollector;
